import { useEffect, useState } from "react";
import { Buton, Kart, MetinAlani, marka } from "@koclukpanel/ui";
import { ApiHatasi, anketiAc, anketiYanitla, type AnketAcilis } from "./api";

/**
 * VELİ MEMNUNİYET ANKETİ — F-SRV-02/03
 *
 * ================================================================
 * GİRİŞ GEREKTİRMEYEN SAYFA
 * ================================================================
 * Şartname bunu açıkça istiyor: "veli panele girmeye üşenirse yanıt
 * oranı düşmesin diye". Güvenlik jetonun kendisinde — tek ankete
 * bağlı, cevaplanınca ve süresi dolunca geçersiz.
 *
 * Sayfa OTURUM SORGUSUNU BEKLEMEDEN açılıyor (`Uygulama.tsx` içinde
 * jeton sayfaları en başta çözülüyor): bekleseydi, hiç hesabı olmayan
 * veli önce giriş ekranını görürdü.
 *
 * ================================================================
 * SORULARI SUNUCU VERİYOR
 * ================================================================
 * Ekranın kendi soru listesi YOK. Şablon veritabanında ve
 * değiştirilebilir; ekran kendi listesini tutsaydı, soru eklendiği
 * gün anket eksik gönderilir ve kimse fark etmezdi.
 */

/** 1–5 ölçeğinin uçları — sayı tek başına ne demek olduğunu söylemiyor. */
const OLCEK_UCLARI = { enAz: "Hiç memnun değilim", enCok: "Çok memnunum" };

export function AnketSayfasi({ jeton }: { jeton: string }) {
  const [anket, setAnket] = useState<AnketAcilis | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [bitti, setBitti] = useState(false);
  const [yanitlar, setYanitlar] = useState<Record<string, number | string>>({});

  useEffect(() => {
    void (async () => {
      try {
        setAnket(await anketiAc(jeton));
      } catch (e) {
        /*
          SUNUCUNUN CÜMLESİ GÖSTERİLİYOR. Üç ayrı durum var —
          "bağlantı geçersiz", "zaten yanıtladınız", "süresi dolmuş" —
          ve üçü de veliye farklı şey söylüyor. Tek bir "açılamadı"
          mesajı, zaten yanıtlamış veliye hata yaptığını düşündürürdü.
        */
        setHata(e instanceof ApiHatasi ? e.message : "Anket açılamadı.");
      } finally {
        setYukleniyor(false);
      }
    })();
  }, [jeton]);

  const gonder = async (): Promise<void> => {
    setGonderiliyor(true);
    setHata(null);
    try {
      await anketiYanitla(jeton, yanitlar);
      setBitti(true);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Yanıt gönderilemedi.");
    } finally {
      setGonderiliyor(false);
    }
  };

  const eksikZorunlu =
    anket?.sorular.some((s) => s.zorunlu && yanitlar[s.anahtar] === undefined) ?? true;

  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-4 p-4">
      <header>
        <p className="text-marka-700 text-sm font-medium">{marka.kisaAd}</p>
        <h1 className="text-notr-900 text-lg font-semibold">Memnuniyet anketi</h1>
      </header>

      {yukleniyor ? (
        <p className="text-notr-600 text-base">Yükleniyor…</p>
      ) : bitti ? (
        <Kart ton="onay" baslik="Teşekkür ederiz">
          <p className="text-notr-800 text-base">
            Yanıtınız kaydedildi. Bu bağlantı artık kullanılamaz.
          </p>
        </Kart>
      ) : hata !== null && anket === null ? (
        <Kart ton="uyari" baslik="Anket açılamadı">
          <p className="text-notr-800 text-base">{hata}</p>
        </Kart>
      ) : anket ? (
        <div className="flex flex-col gap-4">
          <p className="text-notr-700 text-base">
            <strong>{anket.ogrenciAdi}</strong> için birkaç sorumuz var. Giriş yapmanız gerekmiyor;
            yanıtlarınız uzmanınıza <strong>isimsiz</strong> ulaşır.
          </p>

          {anket.sorular.map((s) => (
            <Kart key={s.anahtar} olarak="section">
              <fieldset className="flex flex-col gap-2">
                <legend className="text-notr-900 text-base font-medium">
                  {s.metin}
                  {!s.zorunlu && <span className="text-notr-600 ml-2 text-sm">(isteğe bağlı)</span>}
                </legend>

                {s.tip === "TEXT" ? (
                  <MetinAlani
                    etiket={s.metin}
                    etiketGizli
                    rows={3}
                    value={String(yanitlar[s.anahtar] ?? "")}
                    onChange={(e) => setYanitlar((y) => ({ ...y, [s.anahtar]: e.target.value }))}
                  />
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {/*
                        NPS 0–10, ölçek 1–5. İkisi AYRI ölçek ve
                        sunucu ortalamayı yalnızca 1–5'lerden
                        hesaplıyor — karıştırılırsa ortalama
                        anlamsızlaşır.
                      */}
                      {(s.tip === "NPS" ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [1, 2, 3, 4, 5]).map(
                        (n) => {
                          const secili = yanitlar[s.anahtar] === n;
                          return (
                            <button
                              key={n}
                              type="button"
                              aria-pressed={secili}
                              onClick={() => setYanitlar((y) => ({ ...y, [s.anahtar]: n }))}
                              className={
                                "min-h-dokunma min-w-dokunma rounded-alan text-base font-medium " +
                                "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
                                (secili
                                  ? "bg-marka-600 text-notr-0"
                                  : "bg-notr-100 text-notr-800 hover:bg-notr-200")
                              }
                            >
                              {n}
                            </button>
                          );
                        },
                      )}
                    </div>
                    {s.tip === "SCALE_5" && (
                      /* Sayı tek başına ne demek olduğunu söylemiyor. */
                      <p className="text-notr-600 flex justify-between text-sm">
                        <span>1 — {OLCEK_UCLARI.enAz}</span>
                        <span>5 — {OLCEK_UCLARI.enCok}</span>
                      </p>
                    )}
                    {s.tip === "NPS" && (
                      <p className="text-notr-600 flex justify-between text-sm">
                        <span>0 — Kesinlikle etmem</span>
                        <span>10 — Kesinlikle ederim</span>
                      </p>
                    )}
                  </>
                )}
              </fieldset>
            </Kart>
          ))}

          {hata && (
            <p role="alert" className="rounded-alan bg-hata-500/10 text-hata-700 p-3 text-base">
              {hata}
            </p>
          )}

          <div>
            <Buton
              yukleniyor={gonderiliyor}
              /*
                ZORUNLU SORU EKSİKKEN DÜĞME KAPALI. Sunucu da
                reddediyor; açık bırakmak, on soruyu yanıtlayıp
                gönderdikten sonra hata alan veliyi baştan aratırdı.
              */
              disabled={eksikZorunlu}
              onClick={() => void gonder()}
            >
              Yanıtları gönder
            </Buton>
            <p className="text-notr-600 mt-1.5 text-sm">
              Gönderdikten sonra bu bağlantı kullanılamaz.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
